import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Users, BookOpen, Shield, MagnifyingGlass, Trash, Eye,
  CaretLeft, CaretRight, UserCircle, CircleNotch, Warning, Crown
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { startImpersonation } from '@/utils/permissions'
import type { User } from '@/types'

interface AdminPanelProps {
  user: User
}

type TabId = 'users' | 'tables'

export function AdminPanel({ user }: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>('users')
  const [stats, setStats] = useState<any>(null)

  useEffect(() => {
    apiService.adminGetStats().then(setStats).catch(() => {})
  }, [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown size={28} className="text-amber-500" />
            Painel de Administração
          </h1>
          <p className="text-muted-foreground">
            Gerencie usuários e campanhas do sistema
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Usuários', value: stats.users?.total ?? 0, icon: Users },
            { label: 'Professores', value: stats.users?.professors ?? 0, icon: Shield },
            { label: 'Estudantes', value: stats.users?.students ?? 0, icon: UserCircle },
            { label: 'Campanhas', value: stats.tables ?? 0, icon: BookOpen },
          ].map((s, i) => (
            <Card key={i}>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase">{s.label}</p>
                    <p className="text-2xl font-bold">{s.value}</p>
                  </div>
                  <s.icon size={24} className="text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            activeTab === 'users'
              ? 'bg-card border border-b-0 border-border text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users size={16} className="inline mr-2" />
          Usuários
        </button>
        <button
          onClick={() => setActiveTab('tables')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            activeTab === 'tables'
              ? 'bg-card border border-b-0 border-border text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <BookOpen size={16} className="inline mr-2" />
          Campanhas
        </button>
      </div>

      {activeTab === 'users' && <UsersTab currentUserId={user.id} onStatsChange={() => apiService.adminGetStats().then(setStats)} />}
      {activeTab === 'tables' && <TablesTab onStatsChange={() => apiService.adminGetStats().then(setStats)} />}
    </div>
  )
}

// ─── Users Tab ──────────────────────────────────────────────
function UsersTab({ currentUserId, onStatsChange }: { currentUserId: string; onStatsChange: () => void }) {
  const [users, setUsers] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiService.adminGetUsers({ search: search || undefined, role: roleFilter || undefined, page, per_page: 15 })
      setUsers(data.users)
      setTotalPages(data.pages)
      setTotal(data.total)
    } catch {
      toast.error('Erro ao carregar usuários')
    } finally {
      setLoading(false)
    }
  }, [search, roleFilter, page])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const result = await apiService.adminUpdateUserRole(userId, newRole)
      toast.success(result.message)
      fetchUsers()
      onStatsChange()
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao alterar papel')
    }
  }

  const handleStatusToggle = async (userId: string, currentActive: boolean) => {
    try {
      const result = await apiService.adminUpdateUserStatus(userId, !currentActive)
      toast.success(result.message)
      fetchUsers()
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao alterar status')
    }
  }

  const roleBadge = (role: string) => {
    const config: Record<string, { className: string; label: string }> = {
      superadmin: { className: 'bg-amber-500 text-white', label: 'Admin' },
      professor: { className: 'bg-primary text-primary-foreground', label: 'Professor' },
      estudante: { className: 'bg-secondary text-secondary-foreground', label: 'Estudante' },
    }
    const c = config[role] || config.estudante
    return <Badge className={c.className}>{c.label}</Badge>
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Gerenciar Usuários</CardTitle>
        <CardDescription>{total} usuário(s) encontrado(s)</CardDescription>
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="pl-9"
            />
          </div>
          <select
            value={roleFilter}
            onChange={e => { setRoleFilter(e.target.value); setPage(1) }}
            className="border border-border rounded-md px-3 py-2 text-sm bg-card"
          >
            <option value="">Todos</option>
            <option value="superadmin">Admin</option>
            <option value="professor">Professor</option>
            <option value="estudante">Estudante</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <CircleNotch size={32} className="animate-spin text-muted-foreground" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Warning size={32} className="mx-auto mb-2" />
            <p>Nenhum usuário encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Nome</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Papel</th>
                  <th className="pb-2 font-medium">Login</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="py-3 font-medium">{u.full_name}</td>
                    <td className="py-3 text-muted-foreground">{u.email}</td>
                    <td className="py-3">
                      <select
                        value={u.role}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        disabled={u.id === currentUserId}
                        className="border border-border rounded px-2 py-1 text-xs bg-card disabled:opacity-50"
                      >
                        <option value="estudante">Estudante</option>
                        <option value="professor">Professor</option>
                        <option value="superadmin">Admin</option>
                      </select>
                    </td>
                    <td className="py-3">
                      <Badge variant="outline" className="text-xs">
                        {u.auth_provider === 'google' ? 'Google' : 'Email'}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <Badge variant={u.is_active ? 'default' : 'destructive'} className="text-xs">
                        {u.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="py-3 text-right">
                      {u.id !== currentUserId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStatusToggle(u.id, u.is_active)}
                          className="text-xs"
                        >
                          {u.is_active ? 'Desativar' : 'Ativar'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <CaretLeft size={16} />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Tables Tab ─────────────────────────────────────────────
function TablesTab({ onStatsChange }: { onStatsChange: () => void }) {
  const navigate = useNavigate()
  const [tables, setTables] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchTables = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiService.adminGetTables({ search: search || undefined, page, per_page: 15 })
      setTables(data.tables)
      setTotalPages(data.pages)
      setTotal(data.total)
    } catch {
      toast.error('Erro ao carregar campanhas')
    } finally {
      setLoading(false)
    }
  }, [search, page])

  useEffect(() => { fetchTables() }, [fetchTables])

  const handleDelete = async (tableId: string, tableName: string) => {
    if (!confirm(`Tem certeza que deseja excluir a campanha "${tableName}"?\nEssa ação é irreversível.`)) return
    try {
      const result = await apiService.adminDeleteTable(tableId)
      toast.success(result.message)
      fetchTables()
      onStatsChange()
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao excluir campanha')
    }
  }

  const handleImpersonate = async (tableId: string, tableName: string) => {
    try {
      const result = await apiService.adminImpersonate(tableId)
      startImpersonation(result.token, result.table_id, result.table_name)
      toast.success(`Navegando como Mestre de "${tableName}"`)
      navigate(`/detalhes/${tableId}`)
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao iniciar impersonação')
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Gerenciar Campanhas</CardTitle>
        <CardDescription>{total} campanha(s) encontrada(s)</CardDescription>
        <div className="relative pt-2">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 mt-1 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar campanha..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            className="pl-9"
          />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <CircleNotch size={32} className="animate-spin text-muted-foreground" />
          </div>
        ) : tables.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Warning size={32} className="mx-auto mb-2" />
            <p>Nenhuma campanha encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Nome</th>
                  <th className="pb-2 font-medium">Criador</th>
                  <th className="pb-2 font-medium">Código</th>
                  <th className="pb-2 font-medium">Membros</th>
                  <th className="pb-2 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {tables.map(t => (
                  <tr key={t.id} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="py-3">
                      <div>
                        <p className="font-medium">{t.name}</p>
                        {t.subject && <p className="text-xs text-muted-foreground">{t.subject}</p>}
                      </div>
                    </td>
                    <td className="py-3 text-muted-foreground">{t.creator_name}</td>
                    <td className="py-3">
                      <code className="bg-muted px-2 py-0.5 rounded text-xs">{t.access_code}</code>
                    </td>
                    <td className="py-3">{t.member_count}</td>
                    <td className="py-3 text-right space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleImpersonate(t.id, t.name)}
                        className="text-amber-500 hover:text-amber-400"
                        title="Entrar como Mestre"
                      >
                        <Eye size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(t.id, t.name)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <CaretLeft size={16} />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
